import React, { useMemo } from "react";
import { Empty } from "antd";
import { useTranslation } from "react-i18next";
import type {
  Reservation,
  ReservationStatus,
} from "../../../../utils/api/reservationsAPI";
import ReservationCard from "../ReservationCard/ReservationCard";
import "./ReservationDayView.scss";

type Props = {
  reservations: Reservation[];
  onCardClick: (r: Reservation) => void;
  onStatusChange?: (id: string, status: ReservationStatus) => void;
  onEdit?: (r: Reservation) => void;
};

const ReservationDayView: React.FC<Props> = ({
  reservations,
  onCardClick,
  onStatusChange,
  onEdit,
}) => {
  const { t } = useTranslation();

  // Group reservations by hour for timeline-style view
  const grouped = useMemo(() => {
    const map = new Map<string, Reservation[]>();
    const sorted = [...reservations].sort((a, b) =>
      a.time.localeCompare(b.time)
    );
    for (const r of sorted) {
      const hour = r.time.split(":")[0] + ":00";
      if (!map.has(hour)) map.set(hour, []);
      map.get(hour)!.push(r);
    }
    return map;
  }, [reservations]);

  if (reservations.length === 0) {
    return (
      <div className="rdv-empty">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t("No reservations for this day")}
        />
      </div>
    );
  }

  return (
    <div className="rdv">
      {Array.from(grouped.entries()).map(([hour, items]) => (
        <div key={hour} className="rdv-group">
          <div className="rdv-group__hour">
            <span className="rdv-group__hour-label">{hour}</span>
            <span className="rdv-group__count">
              {items.length} {items.length === 1 ? t("reservation") : t("reservations")}
              {" · "}
              {items.reduce((s, r) => s + r.numberOfGuests, 0)} {t("guests")}
            </span>
          </div>
          <div className="rdv-group__cards">
            {items.map((r) => (
              <ReservationCard
                key={r._id}
                reservation={r}
                onClick={() => onCardClick(r)}
                onStatusChange={onStatusChange}
                onEdit={onEdit}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ReservationDayView;
